import {MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {useMemo, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import type {BlockedPaymentMethod} from '@/services/blockedPaymentMethodService';
import type {AfriexCurrencyCode} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {formatDate} from '@/utils/date';

import type {BlockedPaymentMethodTableProps} from './types';
import {
  transactionTypeToIdentifier,
  transactionTypeToProvider,
  transationTypeToLabel,
} from './types';

export default function BlockedPaymentMethodTable({
  list,
  onRemove,
}: BlockedPaymentMethodTableProps) {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-12 px-4">&nbsp;</Th>
          <Th className="px-2 w-24">Account No</Th>
          <Th className="px-2 w-24">Country</Th>
          <Th className="px-2 w-20">Type</Th>
        </Tr>
      </Thead>
      <TBody>
        {list.map((item: BlockedPaymentMethod, index) => (
          <TRow key={item.id} item={item} onRemove={onRemove} index={index} />
        ))}
      </TBody>
    </Table>
  );
}

const TRow = ({
  item,
  onRemove,
  index,
}: {
  item: BlockedPaymentMethod;
  onRemove: (id: string) => Promise<void>;
  index: number;
}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [isExpanded, setIsExpanded] = useState(false);
  const countryCode = item?.country?.toUpperCase() as AfriexCurrencyCode;
  const label = useMemo(() => {
    return transationTypeToLabel?.[item.type] || 'Payment Method';
  }, [item]);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-[#FCFBF5] text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td className="font-medium">{item?.accountIdentifier}</Td>
        <Td>
          {countryCode} {mapCountryCodeToFlags(countryList)[countryCode]}
        </Td>
        <Td>{label}</Td>
      </Tr>
      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={8} className="p-0 m-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <Summary data={item} onRemove={onRemove} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const Summary = ({
  data,
  onRemove,
}: {
  data: BlockedPaymentMethod;
  onRemove: (id: string) => Promise<void>;
}) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10">
      <DetailTableHeader data={data} onRemove={onRemove} />
      <DetailTable data={data} />
    </div>
  );
};

type DetailTableHeaderProp = {
  data: BlockedPaymentMethod;
  onRemove: (id: string) => Promise<void>;
};

const DetailTableHeader = ({data, onRemove}: DetailTableHeaderProp) => {
  const label = useMemo(() => {
    return transationTypeToLabel?.[data.type] || 'Payment Method';
  }, [data]);

  return (
    <>
      <div className="w-full flex justify-between items-center font-semibold">
        <div className="text-cyan-700 uppercase">Blocked {label} Details</div>
        <div>
          <PermissionsProvider
            permission={AfriexAdminPermission.BlockedPaymentMethodManage}
          >
            <Button
              variant="solid"
              colorScheme="orange"
              onClick={() => {
                // Fire-and-forget: `onRemove` (handleRemoveItem) catches and
                // toasts its own failures, so this only stops an unexpected
                // throw becoming an unhandled rejection.
                onRemove(data?.id as string).catch(() => {});
                return false;
              }}
            >
              Unblock
            </Button>
          </PermissionsProvider>
        </div>
      </div>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-5 w-full" />
    </>
  );
};

const DetailTable = ({data}: {data: BlockedPaymentMethod}) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const providerLabel = useMemo(() => {
    return transactionTypeToProvider?.[data.type] || 'Provider';
  }, [data]);

  // Same dependency as the sibling memo above: the body reads `data.type` off
  // `data`, so `data` is the honest dependency. The value is a lookup in a
  // constant map keyed by `type`, so recomputing on any `data` change returns
  // the same string.
  const identifierLabel = useMemo(() => {
    return transactionTypeToIdentifier?.[data.type] || 'Account Identifier';
  }, [data]);

  const summary = {
    'Date Blocked': formatDate(data?.createdAt || ''),
    [identifierLabel]: data?.accountIdentifier,
    [providerLabel]: data?.provider?.toString(),
    Country: `${data?.country} ${mapCountryCodeToFlags(countryList)[data?.country] ?? ''}`,
    Reason: data.reason,
  };

  return (
    <div className="w-full">
      {Object.entries(summary).map(([key, value], idx) => (
        <div
          key={idx}
          className="flex justify-between py-2 border-b last:border-0 dark:border-dark-borderMid"
        >
          <p className="text-slate-500 text-sm dark:text-slate-100">{key}</p>
          <p className="text-sm font-medium text-gray-700 w-1/2 break-all text-right dark:text-gray-100">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
};
